/**
 * Work Orders — API client.
 * Mirrors the function signatures of src/lib/supabase/workOrders.ts exactly
 * so stores and the sync engine can swap the import without structural changes.
 */
import { apiGet, apiPost, apiPatch, apiDelete } from './client';
import type { WorkOrder, ClosureData } from '@/types/maintenance';

export type { CreateWOInput, BlockWOInput } from '@/lib/supabase/workOrders';

// ─── Queries ──────────────────────────────────────────────────────────────────

export async function fetchWorkOrders(_orgId: string | null): Promise<WorkOrder[]> {
  // orgId resolved server-side from JWT — param kept for signature compatibility
  return apiGet<WorkOrder[]>('/api/work-orders');
}

export interface PaginatedResult<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface WOListFilters {
  page?: number;
  pageSize?: number;
  status?: string;
  mold_id?: string;
  assigned_to?: string;
}

/** Paginated list endpoint — returns envelope with total count. */
export async function fetchWorkOrdersPaginated(
  _orgId: string | null,
  filters: WOListFilters = {},
): Promise<PaginatedResult<WorkOrder>> {
  const params = new URLSearchParams();
  params.set('page', String(filters.page ?? 1));
  params.set('pageSize', String(filters.pageSize ?? 25));
  if (filters.status) params.set('status', filters.status);
  if (filters.mold_id) params.set('mold_id', filters.mold_id);
  if (filters.assigned_to) params.set('assigned_to', filters.assigned_to);
  return apiGet<PaginatedResult<WorkOrder>>(`/api/work-orders?${params.toString()}`);
}

export async function fetchWorkOrder(id: string): Promise<WorkOrder | null> {
  try {
    return await apiGet<WorkOrder>(`/api/work-orders/${id}`);
  } catch {
    return null;
  }
}

export async function generateWOId(_orgId: string | null): Promise<string> {
  const { wo_id } = await apiGet<{ wo_id: string }>('/api/work-orders/next-id');
  return wo_id;
}

// ─── Mutations ────────────────────────────────────────────────────────────────

import type { CreateWOInput as _CreateWOInput } from '@/lib/supabase/workOrders';

export async function createWorkOrderInDB(
  input: _CreateWOInput,
  _userId: string,
  _organizationId: string,
  clientId?: string,
): Promise<WorkOrder> {
  return apiPost<WorkOrder>('/api/work-orders', {
    ...input,
    client_id: clientId,
  });
}

export async function startWorkInDB(
  wo: WorkOrder,
  _userId: string,
  shotCountAtStart?: number,
): Promise<void> {
  await apiPost<void>(`/api/work-orders/${wo.id}/start`, {
    shot_count_at_start: shotCountAtStart,
  });
}

import type { BlockWOInput as _BlockWOInput } from '@/lib/supabase/workOrders';

export async function blockWorkOrderInDB(
  wo: WorkOrder,
  input: _BlockWOInput,
  _userId: string,
): Promise<void> {
  await apiPost<void>(`/api/work-orders/${wo.id}/block`, input);
}

export async function resumeWorkOrderInDB(
  wo: WorkOrder,
  _userId: string,
): Promise<void> {
  await apiPost<void>(`/api/work-orders/${wo.id}/resume`, {});
}

export async function closeWorkOrderInDB(
  wo: WorkOrder,
  closureData: ClosureData,
  _userId: string,
  _orgId?: string | null,
): Promise<{ pending_review?: boolean }> {
  const result = await apiPost<{ pending_review?: boolean } | null>(`/api/work-orders/${wo.id}/close`, closureData);
  return result ?? {};
}

export async function reassignWorkOrderInDB(
  woId: string,
  assignedTo: string | null,
): Promise<void> {
  await apiPatch<void>(`/api/work-orders/${woId}/reassign`, { assigned_to: assignedTo });
}

export async function recordHandoff(
  woId: string,
  notes: string,
  _userId: string,
): Promise<void> {
  await apiPost<void>(`/api/work-orders/${woId}/handoff`, { notes });
}

export async function dismissHandoff(woId: string): Promise<void> {
  await apiDelete<void>(`/api/work-orders/${woId}/handoff`);
}
